/**
 * My Layouts Menu Component
 * 
 * Context menu for user's saved layouts from the database
 * Fetches layouts created by the logged-in user and displays them in categories
 */

/**
 * Convert 18-element TroopNumbers array (database format) to 17-element layoutTroops array (game format)
 * Removes the Senator (index 17) to match the game's format
 * @param {number[]|string} troopNumbers - 18-element array or JSON string
 * @returns {number[]|null} 17-element array or null if invalid
 */
function convertTroopNumbersToLayoutTroops(troopNumbers) {
    if (!troopNumbers) return null;
    
    let array = troopNumbers;
    if (typeof troopNumbers === 'string') {
        try {
            array = JSON.parse(troopNumbers);
        } catch (e) {
            console.error('Failed to parse TroopNumbers:', e);
            return null;
        }
    }
    
    if (!Array.isArray(array)) return null;
    
    // If already 17 elements, return as-is (already in game format)
    if (array.length === 17) return array;
    
    // If 18 elements, remove the last one (Senator at index 17)
    if (array.length === 18) {
        return array.slice(0, 17);
    }
    
    // If not 17 or 18, return null (invalid format)
    return null;
}

export class MyLayoutsMenu {
    constructor() {
        this.element = null;
        this.isOpen = false;
        this.onLoadLayout = null;
        this.layouts = {}; // Will be organized by category (mapType)
        this.isLoading = false;
        this.currentUsername = null;
        
        this.createMenu();
        this.checkAuthStatus();
    }
    
    /**
     * Check if user is logged in and get username
     */
    async checkAuthStatus() {
        try {
            const apiBase = this.getApiBase();
            const response = await fetch(`${apiBase}auth/check.php`);
            
            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // Not JSON (probably HTML 404 page) - treat as not logged in
                this.showNotLoggedIn();
                return;
            }
            
            const data = await response.json();
            if (data.loggedIn && data.user) {
                this.currentUsername = data.user.username;
                await this.loadUserLayouts();
            } else {
                this.showNotLoggedIn();
            }
        } catch (error) {
            // Silently fail - auth endpoint may not exist yet
            this.showNotLoggedIn();
        }
    }
    
    /**
     * Load layouts created by the current user
     */
    async loadUserLayouts() {
        if (!this.currentUsername) {
            return;
        }
        
        this.isLoading = true;
        this.updateMenuContent('<div class="layout-menu-loading">Loading your layouts...</div>');
        
        try {
            const apiBase = this.getApiBase();
            
            // First check for custom folders
            const foldersResponse = await fetch(`${apiBase}user/folders.php?type=layouts`);
            const foldersData = await foldersResponse.json();
            
            if (foldersData.success && foldersData.folders && foldersData.folders.length > 0) {
                // Use custom folder structure
                this.layouts = this.convertFoldersToMenuStructure(foldersData.folders);
                this.rebuildMenuFromFolders();
            } else {
                // Fall back to auto-generated categories
                const response = await fetch(`${apiBase}layouts/search.php?creator=${encodeURIComponent(this.currentUsername)}&limit=1000`);
                const data = await response.json();
                
                if (data.success && data.results && data.results.length > 0) {
                    // Organize layouts by mapType (category)
                    this.layouts = {};
                    data.results.forEach(layout => {
                        const category = layout.mapType || 'General';
                        if (!this.layouts[category]) {
                            this.layouts[category] = [];
                        }
                        this.layouts[category].push(layout);
                    });
                    
                    this.rebuildMenu();
                } else {
                    this.showNoLayouts();
                }
            }
        } catch (error) {
            console.error('Failed to load user layouts:', error);
            this.updateMenuContent('<div class="layout-menu-error">Failed to load layouts</div>');
        } finally {
            this.isLoading = false;
        }
    }
    
    /**
     * Convert folder structure to menu structure
     */
    convertFoldersToMenuStructure(folders) {
        const structure = {};
        
        const processFolder = (folder) => {
            if (!structure[folder.name]) {
                structure[folder.name] = [];
            }
            
            // Add items in this folder
            if (folder.items && folder.items.length > 0) {
                folder.items.forEach(item => {
                    structure[folder.name].push(item);
                });
            }
            
            // Process child folders
            if (folder.children && folder.children.length > 0) {
                folder.children.forEach(child => {
                    processFolder(child);
                });
            }
        };
        
        folders.forEach(folder => {
            processFolder(folder);
        });
        
        return structure;
    }
    
    /**
     * Rebuild menu from folder structure (supports nested folders)
     */
    rebuildMenuFromFolders() {
        if (Object.keys(this.layouts).length === 0) {
            this.showNoLayouts();
            return;
        }
        
        // Build category structure from folders
        const categoryItems = Object.keys(this.layouts).map((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            const layoutItems = layouts.map((layout, layoutIndex) => {
                const uniqueId = `my-layout-${categoryIndex}-${layoutIndex}`;
                const layoutName = layout.LayoutName || `Layout ${layoutIndex + 1}`;
                return `
                    <button 
                        id="${uniqueId}" 
                        class="layout-menu-item" 
                        data-category="${categoryIndex}"
                        data-layout="${layoutIndex}"
                        title="${layout.Remarks || layout.Notes || ''}">
                        ${this.escapeHtml(layoutName)}
                    </button>
                `;
            }).join('');
            
            return `
                <div class="layout-menu-category">
                    <div class="layout-menu-category-header" id="my-layout-category-${categoryIndex}">
                        ${this.escapeHtml(categoryName)} ▶
                    </div>
                    <div class="layout-menu-submenu" id="my-layout-submenu-${categoryIndex}" style="display: none;">
                        ${layoutItems}
                    </div>
                </div>
            `;
        }).join('');
        
        this.updateMenuContent(categoryItems);
        
        // Setup event listeners for category headers
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const header = document.getElementById(`my-layout-category-${categoryIndex}`);
            const submenu = document.getElementById(`my-layout-submenu-${categoryIndex}`);
            
            if (header && submenu) {
                header.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isVisible = submenu.style.display !== 'none';
                    submenu.style.display = isVisible ? 'none' : 'block';
                    header.textContent = isVisible ? `${categoryName} ▶` : `${categoryName} ▼`;
                });
            }
        });
        
        // Setup event listeners for layout items
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            layouts.forEach((layout, layoutIndex) => {
                const btn = document.getElementById(`my-layout-${categoryIndex}-${layoutIndex}`);
                if (btn) {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (this.onLoadLayout) {
                            // Convert database layout to format expected by MainApp
                            const layoutData = {
                                layoutName: layout.LayoutName,
                                layoutString: layout.LongLink || layout.Sharestring || '',
                                layoutRemark: layout.Remarks || '',
                                layoutNote: layout.Notes || '',
                                layoutTroops: convertTroopNumbersToLayoutTroops(layout.TroopNumbers),
                                layoutResources: layout.LayoutResources ? JSON.parse(layout.LayoutResources) : null,
                                mapType: layout.mapType,
                                // Include full layout object for metadata
                                _dbLayout: layout
                            };
                            this.onLoadLayout(layoutData);
                        }
                        this.hide();
                    });
                }
            });
        });
    }
    
    /**
     * Show message when user is not logged in
     */
    showNotLoggedIn() {
        this.updateMenuContent(`
            <div class="layout-menu-message">
                <p>Please log in to view your saved layouts.</p>
            </div>
        `);
    }
    
    /**
     * Show message when user has no layouts
     */
    showNoLayouts() {
        this.updateMenuContent(`
            <div class="layout-menu-message">
                <p>You haven't saved any layouts yet.</p>
                <p style="font-size: 0.9em; color: var(--text-secondary, #999); margin-top: 0.5em;">
                    Save layouts from the City Information section to see them here.
                </p>
            </div>
        `);
    }
    
    /**
     * Update menu content
     */
    updateMenuContent(html) {
        if (this.element) {
            const content = this.element.querySelector('.layout-menu-content');
            if (content) {
                content.innerHTML = html;
            }
        }
    }
    
    /**
     * Rebuild the menu with loaded layouts
     */
    rebuildMenu() {
        if (Object.keys(this.layouts).length === 0) {
            this.showNoLayouts();
            return;
        }
        
        // Build category structure (similar to LayoutMenu)
        const categoryItems = Object.keys(this.layouts).map((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            const layoutItems = layouts.map((layout, layoutIndex) => {
                const uniqueId = `my-layout-${categoryIndex}-${layoutIndex}`;
                const layoutName = layout.LayoutName || `Layout ${layoutIndex + 1}`;
                return `
                    <button 
                        id="${uniqueId}" 
                        class="layout-menu-item" 
                        data-category="${categoryIndex}"
                        data-layout="${layoutIndex}"
                        title="${layout.Remarks || layout.Notes || ''}">
                        ${this.escapeHtml(layoutName)}
                    </button>
                `;
            }).join('');
            
            return `
                <div class="layout-menu-category">
                    <div class="layout-menu-category-header" id="my-layout-category-${categoryIndex}">
                        ${this.escapeHtml(categoryName)} ▶
                    </div>
                    <div class="layout-menu-submenu" id="my-layout-submenu-${categoryIndex}" style="display: none;">
                        ${layoutItems}
                    </div>
                </div>
            `;
        }).join('');
        
        this.updateMenuContent(categoryItems);
        
        // Setup event listeners for category headers
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const header = document.getElementById(`my-layout-category-${categoryIndex}`);
            const submenu = document.getElementById(`my-layout-submenu-${categoryIndex}`);
            
            if (header && submenu) {
                header.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isVisible = submenu.style.display !== 'none';
                    submenu.style.display = isVisible ? 'none' : 'block';
                    header.textContent = isVisible ? `${categoryName} ▶` : `${categoryName} ▼`;
                });
            }
        });
        
        // Setup event listeners for layout items
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            layouts.forEach((layout, layoutIndex) => {
                const btn = document.getElementById(`my-layout-${categoryIndex}-${layoutIndex}`);
                if (btn) {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (this.onLoadLayout) {
                            // Convert database layout to format expected by MainApp
                            const layoutData = {
                                layoutName: layout.LayoutName,
                                layoutString: layout.LongLink || layout.Sharestring || '',
                                layoutRemark: layout.Remarks || '',
                                layoutNote: layout.Notes || '',
                                layoutTroops: convertTroopNumbersToLayoutTroops(layout.TroopNumbers),
                                layoutResources: layout.LayoutResources ? JSON.parse(layout.LayoutResources) : null,
                                mapType: layout.mapType,
                                // Include full layout object for metadata
                                _dbLayout: layout
                            };
                            this.onLoadLayout(layoutData);
                        }
                        this.hide();
                    });
                }
            });
        });
    }
    
    /**
     * Create the menu element
     */
    createMenu() {
        const menu = document.createElement('div');
        menu.id = 'my-layouts-menu';
        menu.className = 'layout-menu';
        
        menu.innerHTML = `
            <div class="layout-menu-content">
                <div class="layout-menu-loading">Loading...</div>
            </div>
        `;
        
        document.body.appendChild(menu);
        this.element = menu;
        
        // Close on outside click
        document.addEventListener('click', (e) => {
            if (this.isOpen && !this.element.contains(e.target)) {
                const btn = document.getElementById('btn-my-layouts');
                if (btn && !btn.contains(e.target)) {
                    this.hide();
                }
            }
        });
        
        this.hide();
    }
    
    /**
     * Show menu at position
     */
    show(x, y) {
        // Refresh layouts when opening
        if (this.currentUsername) {
            this.loadUserLayouts();
        }
        
        this.element.style.display = 'block';
        this.element.style.left = `${x}px`;
        this.element.style.top = `${y}px`;
        this.isOpen = true;
    }
    
    /**
     * Hide menu
     */
    hide() {
        this.element.style.display = 'none';
        this.isOpen = false;
        
        // Close all submenus
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const header = document.getElementById(`my-layout-category-${categoryIndex}`);
            const submenu = document.getElementById(`my-layout-submenu-${categoryIndex}`);
            if (header && submenu) {
                submenu.style.display = 'none';
                header.textContent = `${categoryName} ▶`;
            }
        });
    }
    
    /**
     * Toggle menu visibility
     */
    toggle(x, y) {
        if (this.isOpen) {
            this.hide();
        } else {
            this.show(x, y);
        }
    }
    
    /**
     * Get API base path
     */
    getApiBase() {
        const scriptPath = window.location.pathname;
        const hostname = window.location.hostname;
        
        // Check for root domains: cotg.rlmglobal.com or cotgopt.com (base path is root)
        if (hostname.includes('cotg.rlmglobal.com') || 
            (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
            return '/api/';
        }
        
        // Check for subdirectory paths
        if (scriptPath.includes('/Louopt/')) {
            return '/Louopt/api/';
        } else if (scriptPath.includes('/cotgopt/')) {
            return '/cotgopt/api/';
        } else if (scriptPath.includes('/cotg/')) {
            // rlmglobal.com/cotg/ - subdirectory
            return '/cotg/api/';
        }
        
        // Default to root
        return '/api/';
    }
    
    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

